import { useEffect, useCallback, useState, useRef } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { DidomiSDK } from "@didomi/react";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Helmet } from "react-helmet";
import Header from "../components/Header";
import HeaderByob from "@components/CollectionPageByob/HeaderByob";

import AnnouncementBar from "../components/AnnouncementBar";
import SubscriptionPopupProvider from "../context/SubscriptionPopupProvider";
import AnnouncementBarProvider from "../context/AnnouncementBarProvider";
import DiscountProvider from "../context/DiscountProvider";
import Provider from "../context/provider";
import AuthProvider from "../context/AuthProvider";
import WishlistProvider from "../context/wishlistProvider";
import ShippingDelayProvider from "../context/ShippingDelayProvider";
import { HiddenElementsContextProvider } from "../context/HiddenElements";
import {
  isBrowser,
  createCookie,
  isCrawlerBot,
  createRootDomainCookie,
  getCookie,
  getAllUrlParams,
} from "@helpers/browser";
import GeoProvider from "../context/GeoProvider";
import CartProvider from "../context/CartProvider";
import IntelligemsProvider from "../context/IntelligemsProvider";
import LuxeProvider from "../context/LuxeProvider";
import ExponeaProvider from "../context/ExponeaProvider";

import useResponsiveWithHydrationFix from "../hooks/useResponsiveWithHydrationFix";
import IntelligemsHOC from "../hoc/IntelligemsHOC";
import QuickAccess from "../components/QuickAccess";
import FloatingPromotion from "../components/FloatingPromotion";
import CountryConfirmationPopup from "../components/CountryConfirmationPopup";
import SubscriptionPopUp from "../components/SubscriptionPopUp";
import LuxeReactivationPopup from "@components/luxe/LuxeReactivationPopup";
import OptionalProductsDrawer from "../components/ProductPage/productSummary/optionalProducts/OptionalProductsDrawer";
import Footer from "../components/Footer/footer";
import SideCart from "../components/SideCart";
import MembershipCreditFrame from "../components/luxe/MembershipCreditFrame";
import CartFromParamsOnce from "../components/CartFromParamsOnce";
import LuxeGiftPopup from "@components/LuxeGiftPopup";
import ProductRecommendationsPopup from "@components/ProductRecommendationsPopup";
import StoreLocationProvider from "../context/StoreLocationProvider";

import { alReportError, withALErrorBoundary } from "../helpers/ErrorBoundary/ALErrorBoundary";
import { getGlobalEData } from "../hooks/useLocalizedCartInfo";
import { setGorgiasChatVisibility } from "@helpers/gorgias";

import "./layout.scss";
import "./fonts.scss";
import "./font_guideline.scss";
import "./components.scss";
import "./typography.scss";
import "./orders.scss";

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (typeof query.meta?.sendReport === "function") {
        query.meta.sendReport(error, { name: query.meta?.name, priority: query.meta?.priority });
      } else {
        alReportError(error, query.meta?.name, query.meta?.priority);
      }
    },
  }),
});

function Layout({ children, location }) {
  const [searchVisible, setSearchVisible] = useState(false);
  const [isProductPage, updateIsProductPage] = useState(location.pathname.includes("/products/"));
  const [isCollectionPage, updateIsCollectionPage] = useState(
    location.pathname.includes("/collections/")
  );
  const [isLpPlpPage, updateIsLpPlpPage] = useState(location.pathname.includes("/p/"));
  const [isBlog, updateIsBlog] = useState(location.pathname.includes("/blog/"));
  const [isPage, updateIsPage] = useState(location.pathname.includes("/pages/"));
  const [isByob, updateIsByob] = useState(location.pathname.includes("/byob-"));
  const [isMtoApp, updateIsMtoApp] = useState(location.pathname.includes("/mto-app"));
  const [isTrackingPage, updateIsTrackingPage] = useState(
    location.pathname.includes("/track-my-order/")
  );
  const [isRewardsPage, updateIsRewardPage] = useState(
    /^\/pages\/wishlist|rewards|order-history|account|ana-luisa-luxe/.test(location.pathname)
  );
  const [displayQuickAccess, setDisplayQuickAccess] = useState(true);
  const [displayAB, setDisplayAB] = useState(
    !location.pathname.includes("/lp/") && !location.pathname.includes("/mto-app")
  );
  const [displayNavigation, setDisplayNavigation] = useState(
    !location.pathname.includes("/lp/") && !location.pathname.includes("/mto-app")
  );
  const [displayCredits, setDisplayCredits] = useState(false);
  const [optionalProductsData, setOptionalProductsData] = useState(null); // can be Null or propTypes of OptionalProductsDrawer
  const [recommendedProductsPopupData, setRecommendedProductsPopupData] = useState([]);

  const headerRef = useRef(null);
  const displayFooter = !location.pathname.includes("/lp/") && !isByob && !isMtoApp;
  const hideOnScrollLogo =
    location.pathname.includes("/products/") || location.pathname.includes("/collections/");

  useEffect(() => {
    if (location.pathname.includes("/products/")) {
      updateIsProductPage(true);
    } else {
      updateIsProductPage(false);
    }

    if (location.pathname.includes("/collections/")) {
      updateIsCollectionPage(true);
    } else {
      updateIsCollectionPage(false);
    }

    if (location.pathname.includes("/p/")) {
      updateIsLpPlpPage(true);
    } else {
      updateIsLpPlpPage(false);
    }

    if (/^\/pages\/wishlist|rewards|order-history|account|ana-luisa-luxe/.test(location.pathname)) {
      updateIsRewardPage(true);
    } else {
      updateIsRewardPage(false);
    }

    if (location.pathname.includes("/blog/")) {
      updateIsBlog(true);
    } else {
      updateIsBlog(false);
    }

    if (location.pathname.includes("/pages/")) {
      updateIsPage(true);
    } else {
      updateIsPage(false);
    }
    if (location.pathname.includes("/byob-")) {
      updateIsByob(true);
    } else {
      updateIsByob(false);
    }
    if (location.pathname.includes("/mto-app")) {
      updateIsMtoApp(true);
    } else {
      updateIsMtoApp(false);
    }
    if (location.pathname.includes("/track-my-order/")) {
      updateIsTrackingPage(true);
    } else {
      updateIsTrackingPage(false);
    }

    // Create GE replacement cookie when replacementExpire is in the URL params
    const queryParams = getAllUrlParams(location.href);
    const geData = getGlobalEData();
    if (queryParams.replacementExpire) {
      createRootDomainCookie(
        "GE_Replacement",
        encodeURIComponent(
          `{"glCountry": "${geData?.countryISO.toUpperCase()}", "glCurrency": "${geData?.currencyCode.toUpperCase()}"}`
        ),
        null,
        Number(queryParams.replacementExpire)
      );
    }

    setDisplayCredits(typeof queryParams.credit !== "undefined");

    // AL LUXE cookie creation when coming from Google ADS (for 1 day)
    const disableSubscriptionCookie = getCookie("subscription_disabled");
    if (queryParams.utm_source === "google") {
      if (!disableSubscriptionCookie) {
        createCookie("subscription_disabled", true, 1);
      }
    }

    // Make sure the top AB is displayed
    if (!location.pathname.includes("/collections/") && !location.pathname.includes("/mto-app")) {
      setDisplayAB(true);
    }

    if (location.pathname.includes("/mto-app")) {
      setDisplayAB(false);
      setDisplayNavigation(false);
      setGorgiasChatVisibility(false);
    } else if (isMtoApp && !location.pathname.includes("/lp/")) {
      setDisplayNavigation(true);
    }
  }, [location, isMtoApp]);

  useEffect(() => {
    if (!isBrowser || !isMtoApp) return;

    setGorgiasChatVisibility(false);

    return () => {
      setGorgiasChatVisibility(true);
    };
  }, [isMtoApp]);

  useEffect(() => {
    if (!isBrowser) return;
    const queryParams = getAllUrlParams(location.href);
    if (queryParams.utm_source === "google" || isCrawlerBot() || queryParams.igTest === "false") {
      const handleChange = () => {
        const CONTROL_GROUPS = {
          d2da7c782ce0: "8c8fd2c3a055",
          b13937efb5a7: "cbdc366bb383",
        };
        let cookieValue = {};
        const cookie = getCookie("ig-exps");
        if (cookie) {
          cookieValue = JSON.parse(decodeURIComponent(cookie));
        }
        const hasMissingControlGroupsValues = Object.entries(CONTROL_GROUPS).some(
          ([key, value]) => cookieValue[key] !== value
        );
        if (hasMissingControlGroupsValues) {
          createCookie(
            "ig-exps",
            encodeURIComponent(JSON.stringify({ ...cookieValue, ...CONTROL_GROUPS })),
            1
          );
          // refresh the page to apply the changes
          window.location.reload();
        }
      };

      // Check cookie now
      handleChange();

      // Check cookie changes
      if ("cookieStore" in window && "addEventListener" in window.cookieStore) {
        // CookieStore API is supported
        window.cookieStore.addEventListener("change", handleChange);
        return () => window.cookieStore.removeEventListener("change", handleChange);
      }
      // Fallback to polling
      const interval = setInterval(handleChange, 200);
      return () => clearInterval(interval);
    }
  }, [location]);

  const setSearchPopupVisible = useCallback((visible) => {
    if (isBrowser) {
      document.body.style.overflow = visible ? "hidden" : "";
    }
    setSearchVisible(visible);
  }, []);

  useEffect(() => {
    if (isBrowser) {
      try {
        window.edgetag("init", {
          edgeURL: "https://opcqf.analuisa.com",
          disableConsentCheck: true,
        });
      } catch (error) {
        console.log("error in blotout init", error);
      }

      try {
        const exponeaEmail = isBrowser && window.exponea?.email_id;
        if (exponeaEmail) {
          window.edgetag("user", "email", exponeaEmail);
        }
      } catch (error) {
        console.error(error);
      }
    }
  }, []);

  const quickAccessDiv = useResponsiveWithHydrationFix(
    displayQuickAccess &&
      !isProductPage &&
      !isCollectionPage &&
      !isRewardsPage &&
      !isLpPlpPage &&
      !isBlog &&
      !isPage &&
      !isByob &&
      !isMtoApp &&
      !isTrackingPage && <QuickAccess />,
    null
  );

  let luxeData = [
    {
      key: "exison_plan_settings",
      namespace: "exison",
      storeName: "analuisaparis",
      type: "json_string",
      value:
        '{"status":"1","type":"1","product_name":"AL Luxe","product_price":"39.99","product_gid":7913700229319,"product_variant_id":43790876934343,"tier_name":"AL Luxe","plan_id":1807352007,"plan_name":"billed every 1 month","i_name":"month","i_count":"1","scripts_st_on":0,"otp_enabled":0,"dp":"1","dv":"5.00","dct":"1","dt":"percentage","dat":"all","ds":"0","wo":"0","wo_v":"0.00","wo_t":"percentage","wo_at":"all","wo_collection":[],"wo_product":[],"fs":"0","ft":"1","ft_price":"0.00","ft_type":"1","ft_duration":"3"}',
    },
  ];
  if (process.env.GATSBY_SHOPIFY1_STORE_NAME === "analuisa-staging") {
    luxeData = [
      {
        key: "exison_plan_settings",
        namespace: "exison",
        storeName: "analuisa-staging",
        type: "json_string",
        value:
          '{"status":"1","type":"1","product_name":"AL+","product_price":"2.99","product_gid":8314950320386,"product_variant_id":44196237967618,"tier_name":"VIP","plan_id":2429124866,"plan_name":"billed every 2 day","i_name":"day","i_count":"2","scripts_st_on":0,"otp_enabled":1,"dp":"1","dv":"10.00","dct":"1","dt":"percentage","dat":"all","ds":"0","wo":"0","wo_v":"0.00","wo_t":"percentage","wo_at":"all","wo_collection":[],"wo_product":[],"fs":"0","ft":"1","ft_price":"0.00","ft_type":"1","ft_duration":"3"}',
      },
    ];
  }

  const pinterestTag = [
    {
      name: "p:domain_verify",
      content: "15fa34f13f08d57a4a9343ee44452543",
    },
  ];
  const data = useStaticQuery(graphql`
    query {
      influencer: allContentfulInfluencersLandingPage(filter: { node_locale: { eq: "en-US" } }) {
        edges {
          node {
            influencerName
            campaignName
            discountCode
            platforms
            picture {
              file {
                url
              }
            }
          }
        }
      }
      discount: allContentfulSale(filter: { node_locale: { eq: "en-US" }, title: { ne: "null" } }) {
        edges {
          node {
            node_locale
            title
            countries {
              code
            }
            isLuxe
            isMaster
            discountCode
            matchType
            matchTags
            type
            minimumPurchaseAmount
            freeShippingThreshold
            percentageValue
            defaultGiftProductHandle: giftProductHandle
            giftProductHandles
            giftProductHandleBackup
            fields {
              giftProductsDataRaw
            }
            thresholdConditionQuantity
            thresholdConditionAmount
            thresholdResultPercentage
            checkoutCalloutMinValue
            globalProductText {
              text
              arrivalDate
            }
            colorInformation {
              discountCode
              hpButtonBgColorDesktop
              hpBgColorDesktop
              hpButtonHoverBgColorDesktop
              hpTextColorDesktop
              hpBgColorMobile
              hpButtonBgColorMobile
              hpButtonHoverBgColorMobile
              hpTextColorMobile
              tierBgColorGuest
              tierTextColorGuest
              tierInverseTextColorGuest
              tierBorderColorGuest
              buttonBackgroundColorGuest
              buttonTextColorGuest
              tierBgColorLuxe
              tierTextColorLuxe
              tierInverseTextColorLuxe
              tierBorderColorLuxe
              buttonBackgroundColorLuxe
              buttonTextColorLuxe
              tierBgColorLuxeReached
              tierTextColorLuxeReached
              sidecartBannerTextColor
              checkoutButtonBgColor
              checkoutButtonTextColor
            }
            productTagText
            productTagTextColor {
              mobile
              desktop
            }
            productTagBackgroundColor {
              mobile
              desktop
            }
          }
        }
      }
    }
  `);
  return (
    <GeoProvider>
      <Helmet meta={pinterestTag} />

      <ExponeaProvider>
        <HiddenElementsContextProvider>
          <FloatingPromotion />
          <DidomiSDK
            apiKey="6548bad1-0168-47ce-ac9b-870e1cd36b26"
            noticeId="TthXt7YQ"
            iabVersion={2}
            gdprAppliesGlobally={false}
          />
          <IntelligemsProvider>
            <QueryClientProvider client={queryClient}>
              <DiscountProvider discount={data?.discount ?? null}>
                <Provider
                  setDisplayQuickAccess={setDisplayQuickAccess}
                  setDisplayAB={setDisplayAB}
                  displayAB={displayAB}
                  hideOnScrollLogo={hideOnScrollLogo}
                  setDisplayNavigation={setDisplayNavigation}
                  displayNavigation={displayNavigation}
                  setOptionalProductsData={setOptionalProductsData}
                  recommendedProductsPopupData={recommendedProductsPopupData}
                  setRecommendedProductsPopupData={setRecommendedProductsPopupData}
                  influencersData={data?.influencer?.edges.map((edge) => edge.node) ?? []}
                >
                  <CartProvider>
                    <AuthProvider>
                      <LuxeProvider subscription={luxeData}>
                        <WishlistProvider>
                          <AnnouncementBarProvider>
                            <ShippingDelayProvider>
                              <IntelligemsHOC>
                                <SubscriptionPopupProvider>
                                  <StoreLocationProvider>
                                    <div style={{ position: "relative" }}>
                                      <CartFromParamsOnce
                                        onDone={(res) => {
                                          if (res.checkoutUrl) {
                                            window.location.assign(res.checkoutUrl);
                                          }
                                        }}
                                      />
                                      {!isMtoApp && <AnnouncementBar />}
                                      {!isMtoApp &&
                                        (isByob ? (
                                          <HeaderByob />
                                        ) : (
                                          <Header
                                            ref={headerRef}
                                            searchPopupVisible={searchVisible}
                                            setSearchPopupVisible={setSearchPopupVisible}
                                            displayNavigation={displayNavigation}
                                            setDisplayNavigation={setDisplayNavigation}
                                          />
                                        ))}
                                      <SideCart />
                                      {quickAccessDiv}
                                      <div style={{ position: "relative" }}>{children}</div>
                                      {displayFooter && <Footer />}
                                      {displayCredits && (
                                        <MembershipCreditFrame
                                          style={{
                                            position: "fixed",
                                            zIndex: "999999999999",
                                          }}
                                          setDisplayCredits={setDisplayCredits}
                                        />
                                      )}
                                      {(isProductPage || isLpPlpPage) && (
                                        <OptionalProductsDrawer
                                          isVisible={!!optionalProductsData}
                                          {...(optionalProductsData || {})}
                                        />
                                      )}
                                      <CountryConfirmationPopup />
                                      <SubscriptionPopUp alwaysMounted={isProductPage} />
                                      <LuxeReactivationPopup />
                                      <LuxeGiftPopup />
                                      <ProductRecommendationsPopup />
                                    </div>
                                  </StoreLocationProvider>
                                </SubscriptionPopupProvider>
                              </IntelligemsHOC>
                            </ShippingDelayProvider>
                          </AnnouncementBarProvider>
                        </WishlistProvider>
                      </LuxeProvider>
                    </AuthProvider>
                  </CartProvider>
                </Provider>
              </DiscountProvider>
            </QueryClientProvider>
          </IntelligemsProvider>
        </HiddenElementsContextProvider>
      </ExponeaProvider>
    </GeoProvider>
  );
}

export default withALErrorBoundary({
  name: "Layout",
  priority: "P1",
})(Layout);
